/**
 * CorpBadges.tsx — the corporate dashboard's list badges, for Prospera Admin
 * lists (the corp pages are the reference design):
 *
 *   - OutlineBadge: corp's <Badge variant="outline">, optionally tinted
 *     (e.g. Kind "Product" / "Credit", Disbursal "Retail" in emerald);
 *   - CountPill: corp's rounded count pill with an icon (e.g. Products in
 *     blue with a package, Appt Types in orange with a calendar check);
 *   - FlagIcon: corp's coloured "has X" icon column cell (image in green,
 *     description in purple), empty when the flag is off.
 *
 * Same Tailwind classes as corp's components/ui/badge.tsx and the inline pills
 * in corp's list tables; nothing is imported from the corporate dashboard.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type BadgeTone = 'none' | 'blue' | 'orange' | 'emerald' | 'purple' | 'amber' | 'green' | 'red' | 'gray' | 'indigo' | 'teal';

const OUTLINE_TONES: Record<BadgeTone, string> = {
  none: 'border-slate-200 text-slate-900',
  blue: 'border-blue-300 text-blue-700 bg-blue-50',
  orange: 'border-orange-300 text-orange-700 bg-orange-50',
  emerald: 'border-emerald-300 text-emerald-700 bg-emerald-50',
  purple: 'border-purple-300 text-purple-700 bg-purple-50',
  amber: 'border-amber-300 text-amber-700 bg-amber-50',
  green: 'border-green-300 text-green-700 bg-green-50',
  red: 'border-red-300 text-red-700 bg-red-50',
  gray: 'border-slate-200 text-slate-400',
  indigo: 'border-indigo-300 text-indigo-700 bg-indigo-50',
  teal: 'border-teal-300 text-teal-700 bg-teal-50',
};

/** Corp's outline Badge (rounded-md, text-xs, medium weight). */
export function OutlineBadge({ tone = 'none', className, children }: { tone?: BadgeTone; className?: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium [&>svg]:size-3',
        OUTLINE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const PILL_TONES: Record<Exclude<BadgeTone, 'none'>, string> = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  orange: 'bg-orange-50 border-orange-200 text-orange-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  purple: 'bg-purple-50 border-purple-200 text-purple-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  green: 'bg-green-50 border-green-200 text-green-700',
  red: 'bg-red-50 border-red-200 text-red-700',
  gray: 'bg-slate-50 border-slate-200 text-slate-500',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  teal: 'bg-teal-50 border-teal-200 text-teal-700',
};

/** Corp's rounded count pill: icon + number. Corp shows nothing for zero, so pass hideZero (default). */
export function CountPill({
  tone,
  icon,
  count,
  hideZero = true,
  title,
}: {
  tone: Exclude<BadgeTone, 'none'>;
  icon?: ReactNode;
  count: number;
  hideZero?: boolean;
  title?: string;
}) {
  if (hideZero && count <= 0) return null;
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold [&>svg]:h-3 [&>svg]:w-3',
        PILL_TONES[tone],
      )}
    >
      {icon}
      {count}
    </span>
  );
}

/**
 * Corp's parent-row summary of a flag column: the coloured icon when every
 * (live) child has the flag, grey when only some do, nothing when none do.
 */
export function SummaryIcon({
  flagged,
  total,
  icon,
  className,
  label,
}: {
  flagged: number;
  total: number;
  icon: ReactNode;
  className: string;
  label: string;
}) {
  if (flagged === 0 || total === 0) return null;
  const all = flagged === total;
  return (
    <span
      className={cn('mx-auto flex h-4 w-4 items-center justify-center [&>svg]:h-4 [&>svg]:w-4', all ? className : 'text-slate-300')}
      role="img"
      aria-label={all ? `Every one: ${label}` : `Some: ${label}`}
      data-all={all || undefined}
    >
      {icon}
    </span>
  );
}

/** Corp's coloured flag icon cell (centered); nothing when off. */
export function FlagIcon({ on, icon, className, label }: { on: boolean; icon: ReactNode; className: string; label: string }) {
  if (!on) return null;
  return (
    <span className={cn('mx-auto flex h-4 w-4 items-center justify-center [&>svg]:h-4 [&>svg]:w-4', className)} role="img" aria-label={label}>
      {icon}
    </span>
  );
}
